import {
  FAFSA_SAMPLES_DOCUMENT_FILE_NAME,
  FINANCIAL_STATEMENT_SAMPLES_DOCUMENT_FILE_NAME,
  GRADES_GPA_SAMPLES_DOCUMENT_FILE_NAME,
  PAYSTUB_SAMPLES_DOCUMENT_FILE_NAME,
  REGISTERED_CLASSES_SAMPLES_DOCUMENT_FILE_NAME,
} from './samplePageRegistry';
import { INCENTIVE, PaymentRequestTypeValue, SCHOLARSHIP } from './paymentRequestTypes';

export type RequirementItem = {
  documentId: string;
  title: string;
  description: string;
  note?: string;
  sampleText?: string;
  sampleFileName: string;
  instructions?: string[];
  contactInfo?: Array<{ school: string; url: string }>;
  titleWrapClassName: string;
  maxFiles: number;
};

export type RequirementSection = {
  title: string;
  items: RequirementItem[];
};

export const requirementSections: RequirementSection[] = [
  {
    title: 'Scholarship Requests',
    items: [
      {
        documentId: 'registered-class',
        title: 'List of Registered Classes',
        description: 'Provide either a screenshot or document clearly identifying the number of credits you are enrolled in.',
        sampleText: 'View Sample',
        sampleFileName: REGISTERED_CLASSES_SAMPLES_DOCUMENT_FILE_NAME,
        titleWrapClassName: 'w-40 h-5 relative',
        maxFiles: 2,
        instructions: [
          'This can either be sent in as a screenshot or as a document.',
          'Make sure it is clear and show the number of credits you are taking.',
        ],
      },
      {
        documentId: 'financial-statement',
        title: 'Detailed Financial Statement',
        description:
          'Document that breaks down all charges and payments on your account for a specific term/period. It should show tuition, fees, housing costs, financial aid and other charges.',
        sampleText: 'View Sample',
        sampleFileName: FINANCIAL_STATEMENT_SAMPLES_DOCUMENT_FILE_NAME,
        titleWrapClassName: 'w-44 h-5 relative',
        maxFiles: 2,
        instructions: [
          'It is a document that breaks down all the charges and payments on your account for a specific term or period.',
          'It shows your tuition, fees, housing costs, financial aid, and other charges, which allows you to see exactly where your money is going.',
          'Financial aid and any outside scholarships will need to show up on your bill BEFORE your Boys Town scholarship can be used.',
        ],
        contactInfo: [
          {
            school: 'Metropolitan Community College',
            url: 'https://www.mccneb.edu/getting-started/tuition-financial-aid/financial-aid/contact-financial-aid',
          },
          {
            school: 'University of Nebraska at Omaha',
            url: 'https://www.unomaha.edu/admissions/financial-support-and-scholarships/index.php',
          },
          {
            school: 'University of Nebraska at Lincoln',
            url: 'https://financialaid.unl.edu/',
          },
          {
            school: 'Southeast Community College',
            url: 'https://www.southeast.edu/paying-for-scc/fafsa-information/index.php',
          },
        ],
      },
      {
        documentId: 'fafsa',
        title: 'FAFSA',
        description:
          "To remain eligible for financial aid, you will need to resubmit the Free Application for Federal Student Aid (FAFSA) for every year you're enrolled in school.",
        note: 'Only needed once per school year',
        sampleText: 'View Sample',
        sampleFileName: FAFSA_SAMPLES_DOCUMENT_FILE_NAME,
        titleWrapClassName: 'w-9 h-5 relative',
        maxFiles: 2,
        instructions: [
          "To remain eligible for financial aid, you will need to resubmit the Free Application for Federal Student Aid (FAFSA) for every year you're enrolled in school.",
        ],
      },
      {
        documentId: 'grades-gpa',
        title: 'Grades & GPA',
        description:
          'Official or unofficial transcript from your school that should include your GPA. Blackboard or Canvas do not typically display overall GPA.',
        note: 'Not required for 1st semester students',
        sampleText: 'View Sample',
        sampleFileName: GRADES_GPA_SAMPLES_DOCUMENT_FILE_NAME,
        titleWrapClassName: 'w-20 h-5 relative',
        maxFiles: 2,
        instructions: [
          'This only applies to students who have already completed a semester of school, including summer classes.',
          'This includes an official or unofficial transcript from your school. Make sure it includes your GPA as well.',
          'You generally cannot see your overall GPA directly on Blackboard or Canvas.',
        ],
      },
    ],
  },
  {
    title: 'Incentive Payout Requests',
    items: [
      {
        documentId: 'pay-stub',
        title: 'Most Recent Pay Stubs',
        description: 'Pay stubs must be recent and match the employment information provided in your Successful Futures profile.',
        note: 'Provide all pay stubs for the period you are requesting the incentive for. E.g.: if requesting for first 6 months of employment, provide pay stubs for the entire 6 month period.',
        sampleText: 'View Sample',
        sampleFileName: PAYSTUB_SAMPLES_DOCUMENT_FILE_NAME,
        titleWrapClassName: 'w-40 h-5 relative',
        maxFiles: 5,
      },
    ],
  },
];

export const requiredDocumentsByType: Record<PaymentRequestTypeValue, RequirementItem[]> = {
  [SCHOLARSHIP]: requirementSections[0].items,
  [INCENTIVE]: requirementSections[1].items,
};
